import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

function parseNums(text) {
  return text.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean).map(Number).filter(n => !isNaN(n));
}

export default function MeanMedianModeRangeCalc() {
  const [text, setText] = useState('10, 2, 38, 23, 38, 23, 21');

  const r = useMemo(() => {
    const nums = parseNums(text);
    if (!nums.length) return null;
    const sorted = [...nums].sort((a, b) => a - b);
    const n = nums.length;
    const sum = nums.reduce((a, b) => a + b, 0);
    const mean = sum / n;
    const median = n % 2 === 1 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
    // Mode: most frequent value(s)
    const freq = {}; sorted.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
    const maxFreq = Math.max(...Object.values(freq));
    const modes = maxFreq === 1 ? [] : Object.entries(freq).filter(([, c]) => c === maxFreq).map(([k]) => Number(k));
    const range = sorted[n - 1] - sorted[0];
    // Extras
    const min = sorted[0], max = sorted[n - 1];
    const variance = nums.reduce((a, x) => a + (x - mean) ** 2, 0) / n;
    const sampleVar = n > 1 ? nums.reduce((a, x) => a + (x - mean) ** 2, 0) / (n - 1) : 0;
    return { nums, sorted, n, sum, mean, median, modes, maxFreq, range, min, max, variance, sampleVar };
  }, [text]);

  return (
    <CalcShell look="cnet" title="Mean, Median, Mode, Range Calculator" subtitle="Enter a list of numbers to instantly get the mean, median, mode(s), range, and other summary statistics.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field">
            <label>Data set (separate with commas, spaces, or new lines)</label>
            <textarea
              rows={6}
              className="w-full font-mono text-sm p-2 border border-slate-300 rounded"
              value={text}
              onChange={e => setText(e.target.value)}
              data-testid="mmr-input"
            />
          </div>
        </div>
        <div>
          {r ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200"><div className="text-xs text-blue-700 font-semibold">MEAN</div><div className="text-2xl font-bold" data-testid="mmr-mean">{r.mean.toFixed(4)}</div></div>
                <div className="p-4 rounded-xl bg-green-50 border border-green-200"><div className="text-xs text-green-700 font-semibold">MEDIAN</div><div className="text-2xl font-bold" data-testid="mmr-median">{r.median}</div></div>
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-200"><div className="text-xs text-orange-700 font-semibold">MODE{r.modes.length > 1 ? 'S' : ''}</div><div className="text-lg font-bold" data-testid="mmr-mode">{r.modes.length ? r.modes.join(', ') : 'none'}</div><div className="text-xs">appears {r.maxFreq}×</div></div>
                <div className="p-4 rounded-xl bg-violet-50 border border-violet-200"><div className="text-xs text-violet-700 font-semibold">RANGE</div><div className="text-2xl font-bold" data-testid="mmr-range">{r.range}</div></div>
              </div>
              {/* Bar graph of the data values */}
              <div className="mb-3" data-testid="mmr-bar">
                <div className="text-xs font-semibold text-slate-600 mb-2">Bar chart of your data set</div>
                <svg viewBox={`0 0 ${Math.max(360, r.nums.length * 34)} 180`} className="w-full">
                  {r.nums.map((v, i) => {
                    const h = ((v - r.min) / Math.max(1, r.max - r.min)) * 130 + 10;
                    return (
                      <g key={i}>
                        <rect x={20 + i * 32} y={160 - h} width="24" height={h} fill="#3b82f6" rx="2" />
                        <text x={20 + i * 32 + 12} y={175} fontSize="9" fill="#334155" textAnchor="middle">{v}</text>
                      </g>
                    );
                  })}
                  <line x1="10" y1="160" x2={Math.max(360, r.nums.length * 34) - 10} y2="160" stroke="#94a3b8" />
                </svg>
              </div>
              <table className="data-table" data-testid="mmr-summary">
                <tbody>
                  <tr><td>Count (n)</td><td className="text-right">{r.n}</td></tr>
                  <tr><td>Sum</td><td className="text-right">{r.sum}</td></tr>
                  <tr><td>Minimum</td><td className="text-right">{r.min}</td></tr>
                  <tr><td>Maximum</td><td className="text-right">{r.max}</td></tr>
                  <tr><td>Population Variance (σ²)</td><td className="text-right">{r.variance.toFixed(4)}</td></tr>
                  <tr><td>Population Std. Dev. (σ)</td><td className="text-right">{Math.sqrt(r.variance).toFixed(4)}</td></tr>
                  <tr><td>Sample Variance (s²)</td><td className="text-right">{r.sampleVar.toFixed(4)}</td></tr>
                  <tr><td>Sample Std. Dev. (s)</td><td className="text-right">{Math.sqrt(r.sampleVar).toFixed(4)}</td></tr>
                  <tr><td>Sorted</td><td className="text-right text-xs font-mono">{r.sorted.join(', ')}</td></tr>
                </tbody>
              </table>
            </>
          ) : (
            <div className="text-slate-500">Enter at least one number to see results.</div>
          )}
        </div>
      </div>
      {r && <StepsPanel steps={[
        `Sum = ${r.sum}, n = ${r.n}`,
        `Mean = Sum / n = ${r.sum} / ${r.n} = <b>${r.mean.toFixed(4)}</b>`,
        `Sorted: [${r.sorted.join(', ')}]`,
        `Median = middle value(s) = <b>${r.median}</b>`,
        `Mode(s) = value(s) with highest frequency (${r.maxFreq}×) = <b>${r.modes.length ? r.modes.join(', ') : 'none'}</b>`,
        `Range = Max − Min = ${r.max} − ${r.min} = <b>${r.range}</b>`,
      ]} />}
    </CalcShell>
  );
}
